import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { formatImage } from '@ifeng-fe/three_terminal/es/base/publicMethod/formatImage';
import styles from './index.css';
import { useFixedTitle } from '@src/mobile/event/layout/Nav/hooks';

const cx = classNames.bind(styles);

const getScrollTop = el => {
    let top = el.offsetTop;
    let parent = el.offsetParent;

    while (parent != null) {
        top += parent.offsetTop;
        parent = parent.offsetParent;
    }

    return top;
};

const Live = ({ content }) => {
    const { liveInfo } = content;
    const {
        title,
        poster = 'https://x0.ifengimg.com/fe/custom/2022_08_04/58aef25e928669a97f1481116c466042.png',
    } = liveInfo;

    const [showFixedPlayer, setShowFixedPlayer] = useState(true);

    const videoRef = useRef();

    const [showFixedVideoHeight, setShowFixedVideoHeight] = useState(window.innerHeight);

    useEffect(() => {
        if (videoRef.current) {
            setShowFixedVideoHeight(getScrollTop(videoRef.current) + videoRef.current.offsetHeight);
        }
    }, []);

    const showFixedTitle = useFixedTitle(showFixedVideoHeight);

    useEffect(
        () => {
            if (showFixedTitle) {
                setShowFixedPlayer(true);
            }
        },
        [showFixedTitle],
    );

    const [playStart, setPlayStart] = useState(false);
    const [playing, setPlaying] = useState(false);

    const playerFixed = showFixedPlayer && showFixedTitle && playing;

    useEffect(
        () => {
            if (videoRef.current) {
                if (!playing) {
                    if (playStart) {
                        videoRef.current.pause();
                    }
                } else {
                    videoRef.current.play();

                    if (!playStart) {
                        setPlayStart(true);
                    }
                }
            }
        },
        [playing],
    );

    const [playerMuted, setPlayerMuted] = useState(false);

    const handleFixedCloseClick = () => {
        setShowFixedPlayer(false);

        if (playing) {
            setPlaying(false);
        }
    };

    const hlsRef = useRef(new window.Hls());

    useEffect(
        () => {
            if (videoRef.current) {
                const { liveUrl } = liveInfo;

                if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
                    videoRef.current.src = liveUrl;
                } else if (window.Hls.isSupported()) {
                    hlsRef.current.loadSource(liveUrl);
                    hlsRef.current.attachMedia(videoRef.current);
                }
            }
        },
        [liveInfo],
    );

    const handlePlay = () => {
        if (!playing) {
            setPlaying(true);
        }
    };

    const handlePause = () => {
        if (playing) {
            setPlaying(false);
        }
    };

    return (
        <div className={styles.wrap}>
            <div className={styles.content}>
                <div className={cx('player', { playerFixed })}>
                    <div className={styles.videoWrap}>
                        <video
                            ref={videoRef}
                            poster={poster}
                            muted={playerMuted}
                            x5-playsinline="x5-playsinline"
                            playsInline="playsInline"
                            webkit-playsinline="webkit-playsinline"
                            onPlay={handlePlay}
                            onPause={handlePause}
                        />
                        {playerFixed ? (
                            <div className={styles.playerFixedClose} onClick={handleFixedCloseClick} />
                        ) : (
                            <>
                                {playStart ? (
                                    <div className={styles.action}>
                                        <div
                                            className={cx('playBtn', playing ? 'playBtnPause' : 'playBtnPlay')}
                                            onClick={() => {
                                                setPlaying(prev => !prev);
                                            }}
                                        />
                                        <div className={styles.controls}>
                                            <div
                                                className={cx('volumeBtn', { volumeBtnMuted: playerMuted })}
                                                onClick={() => {
                                                    setPlayerMuted(prev => !prev);
                                                }}
                                            />
                                            <div
                                                className={styles.fullScreenBtn}
                                                onClick={() => {
                                                    if (videoRef.current) {
                                                        if (videoRef.current.requestFullscreen) {
                                                            videoRef.current.requestFullscreen();
                                                        } else if (videoRef.current.webkitRequestFullscreen) {
                                                            videoRef.current.webkitRequestFullscreen();
                                                        } else if (videoRef.current.mozRequestFullScreen) {
                                                            videoRef.current.mozRequestFullScreen();
                                                        } else if (videoRef.current.msRequestFullscreen) {
                                                            videoRef.current.msRequestFullscreen();
                                                        } else if (videoRef.current.webkitEnterFullscreen) {
                                                            videoRef.current.webkitEnterFullscreen();
                                                        } else {
                                                            console.error('全屏失败');
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.poster}>
                                            <img src={formatImage(poster, 1000, 562)} alt="" />
                                        </div>
                                        <div
                                            className={cx('playBtn', 'playBtnPlay')}
                                            onClick={() => {
                                                setPlaying(true);
                                            }}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {title ? <div className={styles.title}>{title}</div> : null}
        </div>
    );
};

Live.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Live);
